import { Project } from '../types';
import { supabase } from './supabase';
import type { ScriptSnapshot } from './scriptPersistence';
import { ScriptConflictError } from './scriptPersistence';

export interface ProjectVersion {
    id: string;
    project_id: string;
    version_number: number;
    label: string | null;
    script_content: unknown;
    title_snapshot: string | null;
    title_page_snapshot: unknown;
    document_snapshot: unknown;
    created_at: string;
}

async function saveProjectFields(id: string, fields: Record<string, unknown>): Promise<void> {
    const { data, error } = await supabase.from('projects')
        .update({ ...fields, updated_at: new Date().toISOString() })
        .eq('id', id).select('id').single();
    if (error) throw error;
    if (!data || data.id !== id) throw new Error('The project was not saved. Check your access and try again.');
}

export const ProjectService = {
    // --- Supabase Project Metadata & Script ---

    getProjects: async (): Promise<Project[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await supabase
            .from('projects')
            .select('*, scenes(*, shots(*))')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('ProjectService: Fetch error', error);
            return [];
        }

        return data as Project[];
    },

    getProject: async (id: string): Promise<Project | null> => {
        const { data, error } = await supabase
            .from('projects')
            .select('*, scenes(*, shots(*))')
            .eq('id', id)
            .single();

        if (error || !data) return null;
        return data as Project;
    },

    createProject: async (title: string, description?: string, projectType?: 'General' | 'Screenplay' | 'Novel' | 'Poetry & Lyrics'): Promise<Project | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        // Default Script Template
        const defaultScript = [
            { id: '1', type: 'scene-heading', content: `INT. ${title.toUpperCase()} - DAY` },
        ];

        const { data, error } = await supabase
            .from('projects')
            .insert({
                user_id: user.id,
                title,
                description,
                script_content: defaultScript,
                project_type: projectType || 'Screenplay'
            })
            .select()
            .single();

        if (error || !data) {
            console.error('ProjectService: Create error', error);
            return null;
        }

        return data as Project;
    },

    deleteProject: async (id: string) => {
        await supabase.from('projects').delete().eq('id', id);
    },

    updateProject: async (id: string, updates: Partial<Project>) => {
        const { error } = await supabase
            .from('projects')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', id);

        if (error) {
            console.error('ProjectService: Update Error', error);
            throw error;
        }
    },

    // --- Verified Cloud Script Content ---

    loadScriptDocument: async (id: string): Promise<Project> => {
        const { data, error } = await supabase.from('projects')
            .select('id,title,project_type,script_content,title_page,metadata,script_revision,script_document')
            .eq('id', id).single();
        if (error) throw error;
        if (!data || data.id !== id) throw new Error('The project could not be loaded.');
        return data as Project;
    },

    saveScriptDocument: async (id: string, snapshot: ScriptSnapshot, revision: number, backupBeforeSave = false): Promise<number> => {
        const { data, error } = await supabase.rpc('save_story_document_v2', {
            p_project_id: id, p_expected_revision: revision,
            p_script_content: snapshot.blocks, p_title_page: snapshot.titlePage,
            p_backup_before_save: backupBeforeSave,
            p_document: snapshot.document ?? null,
        });
        if (error?.code === 'PT409') throw new ScriptConflictError();
        if (error) throw error;
        if (!Number.isSafeInteger(data) || data < revision) throw new Error('The screenplay save was not acknowledged.');
        return data;
    },

    updateScript: async (id: string, blocks: any[]) => {
        await saveProjectFields(id, { script_content: blocks });
    },

    getScript: async (id: string): Promise<any[] | null> => {
        const { data } = await supabase
            .from('projects')
            .select('script_content')
            .eq('id', id)
            .single();

        return data?.script_content || null;
    },

    updateTitlePage: async (id: string, data: any) => {
        // Also update the main project title if title is present in data
        const updates: any = { title_page: data, updated_at: new Date().toISOString() };

        if (data.title) {
            updates.title = data.title;
        }

        await saveProjectFields(id, updates);
    },

    updateTitle: async (id: string, title: string) => {
        await saveProjectFields(id, { title });
    },

    updateProjectState: async (id: string, scrollPosition: number) => {
        await supabase
            .from('projects')
            .update({
                last_scroll_position: scrollPosition,
                last_accessed_at: new Date().toISOString()
            })
            .eq('id', id);
    },

    createScene: async (projectId: string, slug: string): Promise<{ id: string } | null> => {
        const { data, error } = await supabase
            .from('scenes')
            .insert({ project_id: projectId, slug, order_index: 0 })
            .select()
            .single();

        if (error) {
            console.error('ProjectService: Create Scene Error', error);
            return null;
        }
        return data;
    },

    addShot: async (sceneId: string, imageUrl: string, prompt: string, title?: string, tags?: string[]) => {
        // Check if this image already exists in this scene (prevent duplicates)
        const { data: existing } = await supabase
            .from('shots')
            .select('id')
            .eq('scene_id', sceneId)
            .eq('image_url', imageUrl)
            .limit(1);

        if (existing && existing.length > 0) {
            console.log('ProjectService: Shot already exists, skipping duplicate');
            return;
        }

        const { error } = await supabase
            .from('shots')
            .insert({
                scene_id: sceneId,
                image_url: imageUrl,
                description: prompt,
                shot_type: 'image',
                label: title || 'Generated Shot',
                metadata: { tags: tags || [] }
            });

        if (error) {
            console.error('ProjectService: Add Shot Error', error);
        }
    },

    deleteShotByImageUrl: async (imageUrl: string): Promise<boolean> => {
        // Delete any shots that have this image URL
        const { error } = await supabase
            .from('shots')
            .delete()
            .eq('image_url', imageUrl);

        if (error) {
            console.error('ProjectService: Delete Shot Error', error);
            return false;
        }
        return true;
    },

    deleteShotById: async (shotId: string) => {
        // Remove shot from project (does NOT delete from R2 or generations)
        const { error } = await supabase
            .from('shots')
            .delete()
            .eq('id', shotId);

        if (error) {
            console.error('ProjectService: Delete Shot by ID Error', error);
        }
    },

    updateProjectMetadata: async (projectId: string, metadata: any) => {
        const { error } = await supabase
            .from('projects')
            .update({ metadata })
            .eq('id', projectId);

        if (error) {
            console.error('ProjectService: Update Metadata Error', error);
        }
    },


    // --- Versioning ---

    createVersion: async (projectId: string, snapshot: ScriptSnapshot, label?: string): Promise<ProjectVersion> => {
        const { data, error } = await supabase
            .from('project_versions')
            .insert({
                project_id: projectId,
                label: label || 'Snapshot',
                script_content: snapshot.blocks,
                title_snapshot: snapshot.titlePage.title,
                title_page_snapshot: snapshot.titlePage,
                document_snapshot: snapshot.document ?? null,
            }).select('*').single();
        if (error) throw error;
        if (!data || data.project_id !== projectId) throw new Error('The snapshot was not saved.');
        return data as ProjectVersion;
    },

    getVersions: async (projectId: string): Promise<ProjectVersion[]> => {
        const { data, error } = await supabase
            .from('project_versions')
            .select('*')
            .eq('project_id', projectId)
            .order('version_number', { ascending: false });
        if (error) throw error;
        return (data || []) as ProjectVersion[];
    }
};
